// src/components/research/figure.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* The slot every research graphic drops into, so that a diagram added in six
   months lines up with one added today without anyone re-deciding how a figure
   looks. Caption sits below in the small neutral type; the label is the only
   accented part, because a numbered reference is the thing you scan for when
   the prose says "see Figure 2".

   The frame is deliberately unstyled apart from its border: an SVG, a canvas,
   an image and a table should all be able to sit in it without fighting a
   background or a padding decision made here. */
const StyledFigure = styled.figure`
  margin: var(--space-6) 0;
  width: 100%;

  .frame {
    width: 100%;
    overflow: hidden;
    border-radius: var(--border-radius);

    /* Anything wide enough to break the column scrolls inside the figure
       rather than pushing the page sideways. */
    > * {
      max-width: 100%;
    }
  }

  figcaption {
    display: block;
    max-width: var(--measure);
    margin-top: var(--space-3);
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    line-height: var(--lh-body);

    .label {
      margin-right: var(--space-2);
      color: var(--accent);
      font-weight: 500;
    }
  }

  /* Breaks the reading column on wide viewports without breaking the page
     gutters, for a diagram that genuinely needs the room. */
  &.wide {
    @media (min-width: 1080px) {
      width: calc(100% + var(--space-9));
      margin-left: calc(var(--space-9) / -2);
    }
  }
`;

const Figure = ({ label, caption, wide, className, children }) => (
  <StyledFigure className={[wide ? 'wide' : '', className].filter(Boolean).join(' ')}>
    <div className="frame">{children}</div>
    {caption ? (
      <figcaption>
        {label ? <span className="label">{label}</span> : null}
        {caption}
      </figcaption>
    ) : null}
  </StyledFigure>
);

Figure.propTypes = {
  label: PropTypes.string,
  caption: PropTypes.node,
  wide: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Figure;
